/**
 * Build-time feature flags for the mobile app.
 *
 * Flags read `process.env.EXPO_PUBLIC_*` directly. Expo resolves those
 * at bundle time — inlined as literals in a production export, defined
 * on a `process.env` shim in dev — and `app.config.js` seeds
 * `process.env` from `.env.dev` before bundling, the same path
 * `EXPO_PUBLIC_GOOGLE_MAPS_API_KEY` already relies on. Deliberately no
 * `expo-constants` import so this module stays pure and cheap enough
 * to consume from other pure modules (e.g. `tour-steps.ts`).
 *
 * Either way the value is fixed for the lifetime of a bundle: reading
 * it at module scope, in a hook, or in a render is equivalent, and no
 * re-render can be triggered by "changing" one. Flipping a flag means
 * restarting the bundler — and Metro's transform cache does not key on
 * env, so a stale build may need `--clear`.
 */

/**
 * Parses the truthy spellings people actually type in a `.env` file.
 * Anything else — including unset, empty, and `"false"` — is false, so
 * every flag here is opt-in.
 */
function envFlag(raw: string | undefined): boolean {
  if (!raw) return false;
  const v = raw.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

/**
 * Whether the floating layers button renders on the trip-detail screen
 * (bottom-right, next to the add FAB) — the only entry point to the
 * layer toggle menu described by ADR-0040.
 *
 * **Off by default.** The layer prefs themselves stay live: anything
 * already stored in `layerPrefs` keeps applying through `useLayer`,
 * and each layer falls back to its registry `defaultOn` otherwise. So
 * hiding the button hides the *control*, not the feature — a user who
 * had turned a layer off before keeps that state, they just can't
 * change it until the flag is turned back on.
 *
 * Set `EXPO_PUBLIC_SHOW_LAYERS_FAB=true` to bring it back.
 */
export function isLayersFabEnabled(): boolean {
  return envFlag(process.env.EXPO_PUBLIC_SHOW_LAYERS_FAB);
}

/** Test-only seam — lets the unit tests exercise `envFlag`'s parsing
 *  without mutating a `process.env` the bundler may have resolved. */
export const _envFlagForTests = envFlag;
